<template>
    <div class="recordings-manager">
        <div class="header-section">
            <v-card class="search-card">
                <v-card-text class="search-container">
                    <div class="search-and-count">
                        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search" single-line
                            hide-details filled rounded dense class="search-field"
                            background-color="rgba(0, 0, 0, 0.03)" @input="handleSearchInput"></v-text-field>

                        <div class="records-count">
                            <v-chip color="#0d2c6d" text-color="white" small>
                                {{ totalItems.toLocaleString() }}
                            </v-chip>
                            <span class="count-label">Recordings</span>
                        </div>
                    </div>

                    <div class="header-actions">
                        <v-btn-toggle v-model="viewMode" mandatory dense class="view-toggle">
                            <v-btn value="grid" small>
                                <v-icon small>mdi-view-grid</v-icon>
                            </v-btn>
                            <v-btn value="table" small>
                                <v-icon small>mdi-table</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <v-btn v-if="selectedRecords.length > 0" color="error" class="bulk-delete-btn" elevation="1"
                            @click="openBulkDeleteDialog">
                            <v-icon left>mdi-delete-sweep</v-icon>
                            Delete {{ selectedRecords.length }} items
                        </v-btn>

                        <v-btn color="#0d2c6d" class="add-record-btn" elevation="1" @click="addRecordDialog = true">
                            <v-icon left>mdi-plus</v-icon>
                            Add Recording
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <div v-if="viewMode === 'grid'" class="grid-section">
            <div v-if="progressShow" class="loading-container">
                <v-progress-circular indeterminate size="64" width="4" color="#0d2c6d"></v-progress-circular>
                <p class="loading-text">Loading data...</p>
            </div>

            <div v-else-if="records.length === 0" class="no-data-container">
                <v-icon large color="#0d2c6d" class="mb-4">mdi-playlist-music-outline</v-icon>
                <div class="no-data-text">No recordings yet</div>
                <v-btn color="#0d2c6d" outlined @click="refreshDetails()" class="refresh-btn mt-4">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                </v-btn>
            </div>

            <div v-else class="recordings-grid">
                <v-card v-for="(item, index) in records" :key="item._id" class="recording-card" :class="{
                    'playing-card': item.isPlaying,
                    'selected-card': isSelected(item),
                }" elevation="2">
                    <div class="card-header-modern">
                        <div class="checkbox-wrapper">
                            <v-checkbox :input-value="isSelected(item)" @change="toggleSelection(item)" color="white"
                                dark hide-details dense class="modern-checkbox"></v-checkbox>
                        </div>

                        <div class="card-id-modern">
                            <v-icon small color="white" class="audio-icon-modern">mdi-music-circle</v-icon>
                            <span class="id-number-modern">#{{ item.integer_id }}</span>
                            <v-icon v-if="item.isPlaying" small color="#4CAF50" class="playing-pulse-modern">
                                mdi-play-circle
                            </v-icon>
                        </div>
                    </div>

                    <v-card-text class="card-content-modern">
                        <div class="primary-info-modern">
                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-file-music-outline</v-icon>
                                <span class="info-label-modern">Name:</span>
                                <span class="info-value-modern text-truncate" :title="item.record_name_original">
                                    {{ item.record_name_original }}
                                </span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-phone-outline</v-icon>
                                <span class="info-label-modern">Phone:</span>
                                <span class="info-value-modern">{{ item.caller_id }}</span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-calendar-outline</v-icon>
                                <span class="info-label-modern">Recorded:</span>
                                <span class="info-value-modern">{{ item.createdAt }}</span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0"
                                    class="info-icon-modern">mdi-clock-time-four-outline</v-icon>
                                <span class="info-label-modern">Duration:</span>
                                <span class="info-value-modern">{{ item.record_duration }}</span>
                            </div>
                        </div>

                        <v-expand-transition>
                            <div v-if="expandedCards[item._id]" class="expanded-details-modern">
                                <div class="info-row-modern">
                                    <v-icon x-small color="#7986cb"
                                        class="info-icon-modern">mdi-calendar-upload</v-icon>
                                    <span class="info-label-modern">Uploaded:</span>
                                    <span class="info-value-modern">{{ item.customCreatedAt }}</span>
                                </div>

                                <div class="info-row-modern">
                                    <v-icon x-small color="#7986cb" class="info-icon-modern">mdi-tag-outline</v-icon>
                                    <span class="info-label-modern">Type:</span>
                                    <span class="info-value-modern">{{ item.type }}</span>
                                </div>

                                <div class="info-row-modern" v-if="item.subscriber_id?.name">
                                    <v-icon x-small color="#7986cb"
                                        class="info-icon-modern">mdi-account-outline</v-icon>
                                    <span class="info-label-modern">Name:</span>
                                    <span class="info-value-modern">{{ item.subscriber_id.name }}</span>
                                </div>
                            </div>
                        </v-expand-transition>

                        <div v-if="item.type !== 'Private Message'" class="audio-controls-modern">
                            <v-btn icon x-small @click="play(item)" class="control-btn-modern play-btn-modern"
                                :class="{ 'active-play': item.isPlaying && !item.isPaused }">
                                <v-icon x-small>
                                    {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                </v-icon>
                            </v-btn>

                            <v-btn icon x-small @click="stop(item)" class="control-btn-modern stop-btn-modern"
                                :disabled="!item.isPlaying">
                                <v-icon x-small>mdi-stop</v-icon>
                            </v-btn>

                            <v-btn icon x-small @click="download(item)" class="control-btn-modern download-btn-modern">
                                <v-icon x-small>mdi-download</v-icon>
                            </v-btn>
                        </div>
                    </v-card-text>

                    <v-card-actions class="card-actions-modern">
                        <v-btn icon x-small class="expand-btn-modern ml-3" @click="toggleExpand(item._id)">
                            <v-icon small>{{ expandedCards[item._id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>

                        <v-btn v-if="item.type !== 'Private Message'" icon x-small class="stats-btn-modern"
                            @click="openStatisticDialog(item, index)">
                            <v-icon small>mdi-chart-bar</v-icon>
                        </v-btn>

                        <v-spacer></v-spacer>

                        <v-btn icon x-small class="delete-btn-modern" @click="openDeleteDialog(item, index)">
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>

                        <v-tooltip bottom v-if="item.type === 'Await Approval Message'">
                            <template v-slot:activator="{ on }">
                                <v-btn icon x-small v-on="on" @click="forward(item)"
                                    class="control-btn forward-btn mr-3">
                                    <v-icon small>mdi-arrow-right-bold</v-icon>
                                </v-btn>
                            </template>
                            <span>Forward to message extension</span>
                        </v-tooltip>
                    </v-card-actions>
                </v-card>
            </div>

            <div v-if="records.length > 0" class="pagination-section">
                <v-card class="pagination-card">
                    <v-card-text class="pagination-content">
                        <div class="pagination-info">
                            <span>{{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage,
                                totalItems) }} of {{ totalItems.toLocaleString() }}</span>
                        </div>

                        <div class="pagination-controls">
                            <v-btn icon small @click="currentPage = 1; getRecords()" :disabled="currentPage === 1">
                                <v-icon>mdi-chevron-double-right</v-icon>
                            </v-btn>
                            <v-btn icon small @click="currentPage--; getRecords()" :disabled="currentPage === 1">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-btn>

                            <span class="page-number">Page {{ currentPage }} of {{ Math.ceil(totalItems /
                                itemsPerPage) }}</span>

                            <v-btn icon small @click="currentPage++; getRecords()"
                                :disabled="currentPage >= Math.ceil(totalItems / itemsPerPage)">
                                <v-icon>mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn icon small @click="currentPage = Math.ceil(totalItems / itemsPerPage); getRecords()"
                                :disabled="currentPage >= Math.ceil(totalItems / itemsPerPage)">
                                <v-icon>mdi-chevron-double-left</v-icon>
                            </v-btn>
                        </div>

                        <v-select v-model="itemsPerPage" :items="[20, 50, 100, 500]" label="Rows per page" dense
                            outlined hide-details class="items-per-page-select"
                            @change="onItemsPerPageChange"></v-select>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <div class="table-section" v-else>
            <v-card class="table-card">
                <v-data-table :headers="headers" :items="records" v-model="selectedRecords" show-select item-key="_id"
                    class="recordings-table" :loading="progressShow" loading-text="Loading data..."
                    no-data-text="No recordings yet" :server-items-length="totalItems" :items-per-page="itemsPerPage"
                    :page.sync="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                    @update:page="onPageChange" @update:items-per-page="onItemsPerPageChange"
                    @update:sort-by="onSortChange" @update:sort-desc="onSortChange" :footer-props="{
                        'items-per-page-options': [20, 50, 100, 500], 'items-per-page-text': 'Rows per page:', showFirstLastPage: true,
                        firstIcon: 'mdi-chevron-double-left',
                        lastIcon: 'mdi-chevron-double-right',
                        prevIcon: 'mdi-chevron-left',
                        nextIcon: 'mdi-chevron-right'
                    }">

                    <template v-slot:header.data-table-select="{ on, props }">
                        <div>
                            <v-simple-checkbox v-bind="props" v-on="on" color="#0d2c6d"></v-simple-checkbox>
                        </div>
                    </template>

                    <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                        v-slot:[`header.${header.value}`]="{ header }">
                        <div class="custom-header">{{ header.text }}</div>
                    </template>

                    <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index, isSelected, select }">
                        <tr class="table-row" :class="{
                            'playing-row': item.isPlaying,
                            'selected-row': isSelected
                        }">
                            <td class="checkbox-cell">
                                <v-simple-checkbox :value="isSelected" @input="select($event)"
                                    color="#0d2c6d"></v-simple-checkbox>
                            </td>

                            <td class="table-cell">
                                <div class="id-cell">
                                    {{ item.integer_id }}
                                    <v-icon v-if="item.isPlaying" small color="#0d2c6d" class="playing-indicator">
                                        mdi-volume-high
                                    </v-icon>
                                </div>
                            </td>

                            <td class="table-cell">{{ item?.subscriber_id?.name || '' }}</td>
                            <td class="table-cell">{{ item.caller_id }}</td>

                            <td class="table-cell">
                                <div class="type-cell">
                                    <span>{{ item.type }}</span>
                                    <v-tooltip bottom color="#0d2c6d" v-if="item.type === 'Shared Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-icon small color="#0d2c6d" v-on="on" class="info-icon">
                                                mdi-information
                                            </v-icon>
                                        </template>
                                        <span>Shared from system number: {{ item.shared_from_ivr }}</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <td class="table-cell date-cell">{{ item.createdAt }}</td>
                            <td class="table-cell date-cell">{{ item.customCreatedAt }}</td>
                            <td class="table-cell">{{ item.record_duration }}</td>
                            <td class="table-cell">{{ item.record_name_original }}</td>

                            <td class="table-cell">
                                <div class="audio-controls">
                                    <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="stop(item)"
                                                class="table-control-btn table-stop-btn"
                                                :class="{ 'table-active-control': item.isPlaying }">
                                                <v-icon small>mdi-stop</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Stop</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="pause(item)"
                                                class="table-control-btn table-pause-btn"
                                                :class="{ 'table-active-control': item.isPlaying && item.isPaused }">
                                                <v-icon small>mdi-pause</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Pause</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="play(item)"
                                                class="table-control-btn table-play-btn"
                                                :class="{ 'table-active-control': item.isPlaying && !item.isPaused }">
                                                <v-icon small>
                                                    {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.isPlaying && !item.isPaused ? 'Pause' : 'Play' }}</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="download(item)"
                                                class="table-control-btn table-download-btn">
                                                <v-icon small>mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Download</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <td class="table-cell">
                                <div class="action-buttons">
                                    <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)"
                                                class="table-action-btn table-stats-btn">
                                                <v-icon small>mdi-chart-bar</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Listening Statistics</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="openDeleteDialog(item, index)"
                                                class="table-action-btn table-delete-btn">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Delete</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type === 'Await Approval Message'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="forward(item)"
                                                class="control-btn forward-btn">
                                                <v-icon small>mdi-arrow-right-bold</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Forward to message extension</span>
                                    </v-tooltip>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.actions="{ item, index }">
                        <div class="mobile-actions">
                            <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)"
                                        class="action-btn stats-btn">
                                        <v-icon small>mdi-chart-bar</v-icon>
                                    </v-btn>
                                </template>
                                <span>Listening Statistics</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="openDeleteDialog(item, index)"
                                        class="action-btn delete-btn">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type === 'Await Approval Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="forward(item)"
                                        class="control-btn forward-btn">
                                        <v-icon small>mdi-arrow-right-bold</v-icon>
                                    </v-btn>
                                </template>
                                <span>Forward to message extension</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.record_name="{ item }">
                        <div class="mobile-controls">
                            <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="stop(item)" class="control-btn stop-btn"
                                        :class="{ 'active-control': item.isPlaying }">
                                        <v-icon small>mdi-stop</v-icon>
                                    </v-btn>
                                </template>
                                <span>Stop</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="pause(item)" class="control-btn pause-btn"
                                        :class="{ 'active-control': item.isPlaying && item.isPaused }">
                                        <v-icon small>mdi-pause</v-icon>
                                    </v-btn>
                                </template>
                                <span>Pause</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="play(item)" class="control-btn play-btn"
                                        :class="{ 'active-control': item.isPlaying && !item.isPaused }">
                                        <v-icon small>
                                            {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ item.isPlaying && !item.isPaused ? 'Pause' : 'Play' }}</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'Private Message'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="download(item)"
                                        class="control-btn download-btn">
                                        <v-icon small>mdi-download</v-icon>
                                    </v-btn>
                                </template>
                                <span>Download</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.type="{ item }">
                        <div class="type-cell">
                            <span>{{ item.type }}</span>
                            <v-tooltip bottom color="#0d2c6d" v-if="item.type === 'Shared Message'">
                                <template v-slot:activator="{ on }">
                                    <v-icon small color="#0d2c6d" v-on="on" class="info-icon">
                                        mdi-information
                                    </v-icon>
                                </template>
                                <span>Shared from system number: {{ item.shared_from_ivr }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="no-data-container">
                            <v-icon large color="#0d2c6d" class="mb-4">mdi-playlist-music-outline</v-icon>
                            <div class="no-data-text">No recordings yet</div>
                            <v-btn color="#0d2c6d" outlined @click="refreshDetails()" class="refresh-btn mt-4">
                                <v-icon left>mdi-refresh</v-icon>
                                Refresh
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>

        <v-card v-if="currentlyPlaying" class="now-playing-card-modern" elevation="8">
            <v-card-text class="now-playing-content-modern">
                <div class="now-playing-header">
                    <div class="now-playing-info-modern">
                        <v-icon color="#5c6bc0" class="mr-2">mdi-music-circle</v-icon>
                        <div class="now-playing-details">
                            <span class="now-playing-title">
                                {{ currentlyPlaying.record_name_original }}
                            </span>
                            <span class="now-playing-subtitle">
                                File #{{ currentlyPlaying.integer_id }} • {{ currentlyPlaying.caller_id }}
                            </span>
                        </div>
                    </div>
                    <v-btn icon small @click="stop(currentlyPlaying)" class="close-player-btn">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>

                <div class="audio-progress-section">
                    <span class="time-display">{{ formatTime(audioCurrentTime) }}</span>
                    <v-slider v-model="audioCurrentTime" :max="audioDuration" class="audio-seek-slider" hide-details
                        dense @change="seekAudio" @input="onSeekInput" color="#102597"
                        track-color="rgba(92, 107, 192, 0.2)" thumb-label="always">
                        <template v-slot:thumb-label="{ value }">
                            {{ formatTime(value) }}
                        </template>
                    </v-slider>
                    <span class="time-display">{{ formatTime(audioDuration) }}</span>
                </div>

                <div class="now-playing-controls-modern">
                    <v-btn icon @click="pause(currentlyPlaying)" v-if="!currentlyPlaying.isPaused"
                        class="player-control-btn">
                        <v-icon>mdi-pause-circle</v-icon>
                    </v-btn>
                    <v-btn icon @click="play(currentlyPlaying)" v-else class="player-control-btn">
                        <v-icon>mdi-play-circle</v-icon>
                    </v-btn>
                    <v-btn icon @click="stop(currentlyPlaying)" class="player-control-btn">
                        <v-icon>mdi-stop-circle</v-icon>
                    </v-btn>
                    <v-btn icon @click="download(currentlyPlaying)" class="player-control-btn">
                        <v-icon>mdi-download-circle</v-icon>
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>

        <v-overlay :value="overlay" class="loading-overlay">
            <v-progress-circular indeterminate size="64" width="4" color="#0d2c6d"></v-progress-circular>
        </v-overlay>

        <DeleteItemDialog v-model="deleteDialog" v-if="deleteDialog" :item="itemToDelete" :items="itemsToDelete"
            :isBulkDelete="isBulkDelete" actionType="softDelete" @itemDeleted="itemDeleted"
            @itemsDeleted="itemsDeleted">
        </DeleteItemDialog>

        <AddRecordDialog v-model="addRecordDialog" v-if="addRecordDialog" @uploadComplete="recordAdded">
        </AddRecordDialog>

        <ListeningStatisticDialog v-model="listeningStatisticDialog" v-if="listeningStatisticDialog"
            :listeningDetails="listeningDetails" :recordDuration="recordDuration"></ListeningStatisticDialog>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>


<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'
import DeleteItemDialog from '@/components/upload/dialogs/DeleteItemDialog'
import ListeningStatisticDialog from '@/components/upload/dialogs/ListeningStatisticDialog'
import AddRecordDialog from '@/components/upload/dialogs/AddRecordDialog'
import _ from 'lodash'

export default {
    components: {
        SnackBar,
        DeleteItemDialog,
        AddRecordDialog,
        ListeningStatisticDialog
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: 'green',
        snacbarText: '',
        lineNumber: '',
        progressShow: false,
        itemToDelete: {},
        itemsToDelete: [],
        isBulkDelete: false,
        selectedRecords: [],
        deleteDialog: false,
        listeningStatisticDialog: false,
        listeningDetails: [],
        recordDuration: '',
        addRecordDialog: false,
        overlay: false,
        search: '',
        records: [],
        currentlyPlaying: null,

        totalItems: 0,
        currentPage: 1,
        itemsPerPage: 20,
        sortBy: 'customCreatedAt',
        sortDesc: true,

        viewMode: 'grid',
        expandedCards: {},

        audioCurrentTime: 0,
        audioDuration: 0,
        audioUpdateInterval: null,
        isSeeking: false,

        headers: [
            { text: "File No.", value: 'integer_id', align: 'center' },
            { text: 'Name', align: 'center', value: 'subscriber_id.name' },
            { text: 'Phone', value: 'caller_id', align: 'center' },
            { text: 'Extension', value: 'type', align: 'center' },
            { text: 'Recording Date', value: 'createdAt', align: 'center' },
            { text: 'Extension Upload Date', value: 'customCreatedAt', align: 'center' },
            { text: 'Recording Duration', value: 'record_duration', align: 'center' },
            { text: 'Recording Name', value: 'record_name_original', align: 'center' },
            { text: 'Audio', value: 'record_name', align: 'center' },
            { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
        ]
    }),
    methods: {
        // Request records from server with paging, sort, and search
        async getRecords(newSearch = false) {
            try {
                if (newSearch) {
                    this.currentPage = 1
                }

                this.progressShow = true

                const query = new URLSearchParams({
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy || '',
                    sortDesc: this.sortDesc ? 'desc' : 'asc',
                    search: this.search || ''
                }).toString()

                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/records/get_records?${query}`

                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )

                const jsonObject = await res.json()
                this.progressShow = false

                if (res.status >= 400) {
                    this.showSnackBar('Error retrieving data', 'red')
                } else if (res.status === 200) {
                    // Expected format: { data: [...], totalItems: number }
                    this.records = jsonObject.data || []
                    this.totalItems = jsonObject.totalItems || 0
                    this.formatDetails()
                }
            } catch (error) {
                this.progressShow = false
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        formatDetails() {
            this.records = this.records.map((record, index) => {
                return {
                    ...record,
                    customCreatedAt: utils.getFormatDate(record.customCreatedAt),
                    createdAt: utils.getCurrentDate(record.createdAt),
                    record_duration_original: record.record_duration,
                    record_duration: this.formatDuration(record.record_duration),
                    type: this.getRecordTypeText(record.type),
                    caller_id:
                        record.caller_id === 'WEBSITE'
                            ? 'Admin Panel'
                            : record.caller_id,
                    position:
                        (this.currentPage - 1) * this.itemsPerPage + (index + 1),
                    isPlaying: false,
                    isPaused: false
                }
            })
        },

        formatDetailsSingle(record) {
            return {
                ...record,
                customCreatedAt: utils.getCurrentDate(record.customCreatedAt),
                createdAt: utils.getCurrentDate(record.createdAt),
                record_duration: this.formatDuration(record.record_duration),
                type: this.getRecordTypeText(record.type),
                position: this.records.length + 1,
                caller_id: 'Admin Panel',
                isPlaying: false,
                isPaused: false
            }
        },

        getRecordTypeText(type) {
            const types = {
                regular: 'Extension 1 Message',
                private: 'Private Message',
                managers: 'Manager Message',
                pending: 'Scheduled Message',
                shared: 'Shared Message',
                survey: 'Survey Message',
                await_approval: 'Await Approval Message',
            };

            return types[type] || 'הודעה משותפת';
        },

        async setAudio(record) {
            try {
                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/` + record.record_name
                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )
                const jsonObject = await res.blob()

                if (res.status >= 400) {
                    this.showSnackBar('Error playing recording', 'red')
                } else if (res.status === 200) {
                    const url = URL.createObjectURL(jsonObject)
                    record.record_link = url
                    record.record_audio = new Audio(url)
                    const index = this.records.findIndex(
                        record1 => record1._id === record._id
                    )
                    Object.assign(this.records[index], record)
                }
            } catch (error) {
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        async setNewAudio(record) {
            try {
                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/` + record.record_name
                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )
                const jsonObject = await res.blob()

                if (res.status >= 400) {
                    // this.showSnackBar("Error playing recording", "red");
                } else if (res.status === 200) {
                    const url = URL.createObjectURL(jsonObject)
                    record.record_link = url
                    record.record_audio = new Audio(url)
                    record = this.formatDetailsSingle(record)
                    this.records.push(record)
                }
            } catch (error) {
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        formatDuration(seconds) {
            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            const sec = seconds % 60

            const paddedHours = hours.toString().padStart(2, '0')
            const paddedMinutes = minutes.toString().padStart(2, '0')
            const paddedSeconds = sec.toString().padStart(2, '0')

            return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
        },

        showSnackBar(message, color) {
            this.snacbarText = message
            this.snackbarColorBt = color
            this.snackbar = true
        },

        async play(item) {
            if (this.currentlyPlaying && this.currentlyPlaying !== item) {
                this.stop(this.currentlyPlaying)
            }

            if (!item.record_audio) {
                this.overlay = true
                await this.setAudio(item)
                this.overlay = false
            }

            if (item.isPaused) {
                item.record_audio.play()
                item.isPaused = false
            } else {
                item.record_audio.play()
                item.isPlaying = true
                item.isPaused = false
                this.currentlyPlaying = item

                this.audioDuration = item.record_audio.duration || item.record_duration_original || 0
                this.startAudioTracking()

                item.record_audio.addEventListener('ended', () => {
                    this.stop(item)
                })
                item.record_audio.addEventListener('error', () => {
                    this.stop(item)
                    this.showSnackBar('Error: Invalid audio file', 'red')
                })
                item.record_audio.addEventListener('loadedmetadata', () => {
                    this.audioDuration = item.record_audio.duration
                })
            }
        },

        stop(item) {
            if (item.record_audio) {
                item.record_audio.pause()
                item.record_audio.currentTime = 0
                item.isPlaying = false
                item.isPaused = false

                if (this.currentlyPlaying === item) {
                    this.currentlyPlaying = null
                    this.stopAudioTracking()
                    this.audioCurrentTime = 0
                    this.audioDuration = 0
                }
            }
        },

        pause(item) {
            if (item.record_audio && item.isPlaying) {
                item.record_audio.pause()
                item.isPaused = true
            }
        },

        async download(item) {

            const DOWNLOAD_COOLDOWN_MS = 30 * 60 * 1000; // חצי שעה
            const LAST_DOWNLOAD_KEY = 'last_audio_download_at';

            const now = Date.now();
            const lastDownloadAt = Number(localStorage.getItem(LAST_DOWNLOAD_KEY));

            // בדיקת cooldown
            if (lastDownloadAt && now - lastDownloadAt < DOWNLOAD_COOLDOWN_MS) {
                const minutesLeft = Math.ceil(
                    (DOWNLOAD_COOLDOWN_MS - (now - lastDownloadAt)) / 60000
                );
                this.showSnackBar(
                    `You can download a file only once every 30 minutes. Please try again in ${minutesLeft} minutes.`, 'red');
                return;
            }

            if (!item.record_audio) {
                this.overlay = true
                await this.setAudio(item)
                this.overlay = false
            }

            const downloadLink = document.createElement('a')
            downloadLink.href = item.record_link

            if (!item.record_name_original) {
                const startIndex = item.record_name.indexOf('users_records/')
                downloadLink.download = item.record_name
                    .substring(
                        startIndex + 'users_records/'.length
                    )
                    .replace(/\//g, '_')
            } else {
                downloadLink.download = item.record_name_original
            }

            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)

            // שמירת זמן ההורדה
            localStorage.setItem(LAST_DOWNLOAD_KEY, String(now));
        },


        openDeleteDialog(item, index) {
            this.itemToDelete = item
            this.isBulkDelete = false
            this.deleteDialog = true
        },

        openBulkDeleteDialog() {
            this.itemsToDelete = [...this.selectedRecords]
            this.isBulkDelete = true
            this.deleteDialog = true
        },

        itemDeleted(id, text, color) {
            if (id === '') {
                this.showSnackBar(text, color)
                return
            }

            const index = this.records.findIndex(record => record._id === id)

            if (this.records[index] === this.currentlyPlaying) {
                this.currentlyPlaying = null
            }

            this.records.splice(index, 1)
            this.showSnackBar(text, color)
        },

        itemsDeleted(ids, text, color) {
            if (!ids || ids.length === 0) {
                this.showSnackBar(text, color)
                return
            }

            ids.forEach(id => {
                const index = this.records.findIndex(record => record._id === id)
                if (index !== -1) {
                    if (this.records[index] === this.currentlyPlaying) {
                        this.currentlyPlaying = null
                    }
                    this.records.splice(index, 1)
                }
            })

            this.selectedRecords = []
            this.showSnackBar(text, color)
        },

        recordAdded(record, text, color) {
            // After upload - refresh from server (including paging)
            this.getRecords()
        },

        openStatisticDialog(item, index) {
            this.listeningDetails = item.listening
            this.recordDuration = item.record_duration_original
            this.listeningStatisticDialog = true
        },

        refreshDetails() {
            this.getRecords()
        },

        // ==== SERVER PAGING EVENTS ====
        onPageChange(newPage) {
            this.currentPage = newPage
            this.getRecords()
        },

        onItemsPerPageChange(newItemsPerPage) {
            this.itemsPerPage = newItemsPerPage
            this.getRecords(true)
        },

        onSortChange() {
            this.debouncedGetRecords(true)
        },

        handleSearchInput() {
            if (this.search) {
                this.debouncedGetRecords(true)
            } else {
                this.getRecords(true)
            }
        },

        isSelected(item) {
            return this.selectedRecords.some(record => record._id === item._id)
        },

        toggleSelection(item) {
            const index = this.selectedRecords.findIndex(record => record._id === item._id)
            if (index > -1) {
                this.selectedRecords.splice(index, 1)
            } else {
                this.selectedRecords.push(item)
            }
        },

        toggleExpand(cardId) {
            this.$set(this.expandedCards, cardId, !this.expandedCards[cardId])
        },

        startAudioTracking() {
            this.stopAudioTracking() // Clear any existing interval
            this.audioUpdateInterval = setInterval(() => {
                if (this.currentlyPlaying?.record_audio && !this.isSeeking) {
                    this.audioCurrentTime = this.currentlyPlaying.record_audio.currentTime
                }
            }, 100)
        },

        stopAudioTracking() {
            if (this.audioUpdateInterval) {
                clearInterval(this.audioUpdateInterval)
                this.audioUpdateInterval = null
            }
        },

        seekAudio(value) {
            if (this.currentlyPlaying?.record_audio) {
                this.currentlyPlaying.record_audio.currentTime = value
                this.isSeeking = false
            }
        },

        onSeekInput() {
            this.isSeeking = true
        },

        formatTime(seconds) {
            if (!seconds || isNaN(seconds)) return '00:00'
            const mins = Math.floor(seconds / 60)
            const secs = Math.floor(seconds % 60)
            return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        },
        async forward(item) {
            try {
                this.progressShow = true;
                const datesJSON = JSON.stringify({ record: item });
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/records/forward_waiting_approval_to_exten_1`;
                const res = await fetch(api, ApiServices.requestOptions("POST", datesJSON, token));

                if (res.status >= 400) {
                    this.showSnackBar("Error forwarding file", "red");
                } else if (res.status === 200) {
                    const index = this.records.findIndex(record => record._id == item._id);
                    this.records.splice(index, 1);
                    if (index !== -1) {
                        item.type = 'regular';
                        this.$set(this.records, index, item);
                    }
                    this.showSnackBar("File forwarded successfully to message extension", "green");
                }
                this.progressShow = false;
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error forward file: " + error, "red");
            }
        },
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber()
        this.getRecords()
        this.debouncedGetRecords = _.debounce(this.getRecords, 700)
    },
    beforeDestroy() {
        if (this.currentlyPlaying) {
            this.stop(this.currentlyPlaying)
        }
        this.stopAudioTracking()
    }
}
</script>


<style scoped>
/* Keeping styles exactly as they were */
.recordings-manager {
    padding: 0 16px;
}

.header-section {
    margin-bottom: 24px;
}

.search-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.search-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
}

.search-and-count {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    flex: 1;
}

.search-field {
    max-width: 300px;
}

.records-count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.count-label {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
}

/* Added styles for header actions container */
.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

/* Added styles for view toggle */
.view-toggle {
    border-radius: 8px;
    overflow: hidden;
}

/* Updated button colors to match original table styling */
.bulk-delete-btn {
    background-color: #f44336 !important;
    color: white !important;
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    transition: all 0.3s ease;
}

.bulk-delete-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(244, 67, 54, 0.3) !important;
}

.add-record-btn {
    background-color: #0d2c6d !important;
    color: white !important;
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    transition: all 0.3s ease;
}

.add-record-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(13, 44, 109, 0.3) !important;
}

/* Grid View Styles - Modern Design */
.grid-section {
    margin-bottom: 32px;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    gap: 16px;
}

.loading-text {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    margin: 0;
}

/* Modern card grid with cleaner spacing */
.recordings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
}

.recording-card {
    border-radius: 16px;
    border: 2px solid transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    height: fit-content;
    background: #ffffff;
}

.recording-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(92, 107, 192, 0.15) !important;
    border-color: rgba(92, 107, 192, 0.2);
}

.playing-card {
    border-color: #5c6bc0;
    box-shadow: 0 8px 24px rgba(92, 107, 192, 0.25) !important;
}

.selected-card {
    border-color: rgba(92, 107, 192, 0.4);
    background-color: rgba(92, 107, 192, 0.02);
}

/* Modern card header with cleaner gradient */
.card-header-modern {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: linear-gradient(135deg, #102597 0%, #2e40a7c5 100%);
    color: white;
    min-height: 40px;
}

.modern-checkbox {
    margin: 0 !important;
    padding: 0 !important;
}

.modern-checkbox>>>.v-input__slot {
    margin: 0 !important;
    padding: 0 !important;
}

.modern-checkbox>>>.v-icon {
    color: white !important;
    font-size: 22px !important;
}

.card-id-modern {
    display: flex;
    align-items: center;
    gap: 8px;
}

.audio-icon-modern {
    font-size: 20px !important;
}

.id-number-modern {
    font-size: 14px;
    font-weight: 600;
}

.playing-pulse-modern {
    animation: pulse-modern 1.5s ease-in-out infinite;
}

@keyframes pulse-modern {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.7;
        transform: scale(1.1);
    }
}

/* Cleaner card content */
.card-content-modern {
    padding: 14px !important;
}

.primary-info-modern {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.info-row-modern {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}

.info-icon-modern {
    flex-shrink: 0;
}

.info-label-modern {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    min-width: 42px;
}

.info-value-modern {
    color: rgba(0, 0, 0, 0.87);
    font-weight: 500;
    flex: 1;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.expanded-details-modern {
    padding: 12px;
    margin-top: 10px;
    border-top: 1px solid rgba(92, 107, 192, 0.1);
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: linear-gradient(to bottom, rgba(92, 107, 192, 0.02), transparent);
    border-radius: 8px;
}

/* Modern audio controls with soft, transparent colors */
.audio-controls-modern {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    background: linear-gradient(135deg, rgba(92, 107, 192, 0.05), rgba(92, 107, 192, 0.02));
    border-radius: 12px;
    margin-top: 10px;
}

.control-btn-modern {
    width: 32px !important;
    height: 32px !important;
    border-radius: 50%;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.control-btn-modern:hover:not(:disabled) {
    transform: scale(1.15);
}

.control-btn-modern:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.play-btn-modern {
    background-color: rgba(76, 175, 80, 0.12) !important;
    color: #4caf50 !important;
}

.play-btn-modern:hover:not(:disabled) {
    background-color: rgba(76, 175, 80, 0.2) !important;
}

.play-btn-modern.active-play {
    background-color: rgba(255, 152, 0, 0.15) !important;
    color: #ff9800 !important;
}

.stop-btn-modern {
    background-color: rgba(244, 67, 54, 0.12) !important;
    color: #f44336 !important;
}

.stop-btn-modern:hover:not(:disabled) {
    background-color: rgba(244, 67, 54, 0.2) !important;
}

.download-btn-modern {
    background-color: rgba(33, 150, 243, 0.12) !important;
    color: #2196f3 !important;
}

.download-btn-modern:hover:not(:disabled) {
    background-color: rgba(33, 150, 243, 0.2) !important;
}

/* Modern card actions */
.card-actions-modern {
    padding: 8px 12px !important;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    min-height: 44px;
}

.expand-btn-modern {
    background-color: rgba(92, 107, 192, 0.08) !important;
    color: #5c6bc0 !important;
    transition: all 0.2s ease;
}

.expand-btn-modern:hover {
    background-color: rgba(92, 107, 192, 0.15) !important;
}

.stats-btn-modern {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
    transition: all 0.2s ease;
}

.stats-btn-modern:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.delete-btn-modern {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
    transition: all 0.2s ease;
}

.delete-btn-modern:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

/* Pagination Styles */
.pagination-section {
    margin-top: 24px;
}

.pagination-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.pagination-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 20px !important;
}

.pagination-info {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.page-number {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.8);
    padding: 0 12px;
}

.items-per-page-select {
    max-width: 120px;
}

/* Table buttons with soft, transparent colors like original */
.table-control-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.table-stop-btn {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
}

.table-stop-btn:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

.table-stop-btn.table-active-control {
    background-color: rgba(244, 67, 54, 0.2) !important;
}

.table-pause-btn {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
}

.table-pause-btn:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.table-pause-btn.table-active-control {
    background-color: rgba(255, 152, 0, 0.2) !important;
}

.table-play-btn {
    background-color: rgba(76, 175, 80, 0.08) !important;
    color: #4caf50 !important;
}

.table-play-btn:hover {
    background-color: rgba(76, 175, 80, 0.15) !important;
}

.table-play-btn.table-active-control {
    background-color: rgba(76, 175, 80, 0.2) !important;
}

.table-download-btn {
    background-color: rgba(33, 150, 243, 0.08) !important;
    color: #2196f3 !important;
}

.table-download-btn:hover {
    background-color: rgba(33, 150, 243, 0.15) !important;
}

.table-action-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.table-stats-btn {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
}

.table-stats-btn:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.table-delete-btn {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
}

.table-delete-btn:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

/* Table Section Styles */
.table-section {
    margin-bottom: 32px;
}

.table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.recordings-table {
    width: 100%;
}

.custom-header {
    font-size: 14px;
    font-weight: 600;
    color: #0d2c6d;
    text-align: center;
}

.table-row {
    transition: all 0.3s ease;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.03) !important;
}

.playing-row {
    background-color: rgba(13, 44, 109, 0.08) !important;
    border-left: 4px solid #0d2c6d;
}

.playing-row:hover {
    background-color: rgba(13, 44, 109, 0.12) !important;
}

.selected-row {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.8);
    text-align: center !important;
    padding: 12px 8px !important;
}

.checkbox-cell {
    width: 48px;
    padding: 8px !important;
}

.id-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.playing-indicator {
    animation: pulse 1.5s ease-in-out infinite alternate;
}

@keyframes pulse {
    from {
        opacity: 0.6;
    }

    to {
        opacity: 1;
    }
}

.type-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.audio-controls,
.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.mobile-controls,
.mobile-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

/* Enhanced Now Playing Card with modern design */
.now-playing-card-modern {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
    border-radius: 16px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fd 100%);
    box-shadow: 0 12px 40px rgba(92, 107, 192, 0.25) !important;
    border: 1px solid rgba(92, 107, 192, 0.1);
    min-width: 360px;
    max-width: 400px;
}

.now-playing-content-modern {
    padding: 16px !important;
}

.now-playing-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
}

.now-playing-info-modern {
    display: flex;
    align-items: flex-start;
    flex: 1;
    gap: 12px;
}

.now-playing-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.now-playing-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.4;
}

.now-playing-subtitle {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.close-player-btn {
    background-color: rgba(0, 0, 0, 0.04) !important;
    transition: all 0.2s ease;
}

.close-player-btn:hover {
    background-color: rgba(0, 0, 0, 0.08) !important;
}

/* Audio progress section with seek bar */
.audio-progress-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.time-display {
    font-size: 12px;
    font-weight: 600;
    color: #102597;
    min-width: 40px;
    text-align: center;
}

.audio-seek-slider {
    flex: 1;
    margin: 0 !important;
}

.audio-seek-slider>>>.v-slider__track-container {
    height: 6px;
}

.audio-seek-slider>>>.v-slider__track-fill {
    border-radius: 3px;
}

.audio-seek-slider>>>.v-slider__track-background {
    border-radius: 3px;
}

.audio-seek-slider>>>.v-slider__thumb {
    width: 16px;
    height: 16px;
}

.audio-seek-slider>>>.v-slider__thumb-label {
    font-size: 11px;
    font-weight: 600;
}

.now-playing-controls-modern {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.player-control-btn {
    background-color: rgba(92, 107, 192, 0.08) !important;
    color: #5c6bc0 !important;
    transition: all 0.25s ease;
}

.player-control-btn:hover {
    background-color: rgba(92, 107, 192, 0.15) !important;
    transform: scale(1.1);
}

.player-control-btn>>>.v-icon {
    font-size: 28px;
}

.loading-overlay {
    z-index: 1000;
}

.no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
}

.no-data-text {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 16px;
}

.refresh-btn {
    text-transform: none;
    letter-spacing: 0;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.refresh-btn:hover {
    background-color: rgba(13, 44, 109, 0.05);
    transform: translateY(-2px);
}

/* Responsive adjustments */
@media (max-width: 960px) {
    .search-container {
        flex-direction: column;
        align-items: stretch;
    }

    .search-and-count {
        flex-direction: column;
        align-items: stretch;
    }

    .search-field {
        max-width: 100%;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .view-toggle {
        order: -1;
        width: 100%;
        justify-content: center;
    }

    .bulk-delete-btn,
    .add-record-btn {
        flex: 1;
    }

    .recordings-grid {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }

    .pagination-content {
        flex-direction: column;
        align-items: stretch;
    }

    .pagination-controls {
        justify-content: center;
    }

    .items-per-page-select {
        max-width: 100%;
    }

    .now-playing-card-modern {
        bottom: 16px;
        right: 16px;
        left: 16px;
        min-width: auto;
        max-width: none;
    }
}

@media (max-width: 600px) {
    .recordings-manager {
        padding: 0 8px;
    }

    .recordings-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}

.v-data-table>>>td {
    text-align: center !important;
}

.v-data-table>>>th {
    background-color: rgba(0, 0, 0, 0.116) !important;
    border-left: 1px solid white !important;
}

.v-data-table>>>.v-data-table__wrapper table {
    table-layout: fixed;
}
</style>